/**
 * The accounting screens' request helper.
 *
 * It keeps the call shape those screens were written against (including
 * multipart upload and file download, which axios handles poorly) but has no
 * session of its own: the token, company and financial year come from the
 * platform's single session, and a rejected token ends that single session.
 */

import { expireSession, getScope, getToken, resolveApiPath } from '@/lib/api';
import { announceDataChanged } from '@/lib/dataChanged';

export interface ApiError extends Error {
  status: number;
  code: string;
  details?: any;
}

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

export const currentScope = () => ({ token: getToken(), ...getScope() });

function headers(extra: Record<string, string> = {}) {
  const h: Record<string, string> = { ...extra };
  const token = getToken();
  const { companyId, fyId } = getScope();
  if (token) h.Authorization = `Bearer ${token}`;
  if (companyId) h['X-Company-Id'] = String(companyId);
  if (fyId) h['X-Fy-Id'] = String(fyId);
  return h;
}

async function handle(res: Response) {
  if (res.status === 204) return null;

  const type = res.headers.get('content-type') || '';
  if (!type.includes('application/json')) {
    if (!res.ok) {
      const err = new Error(await res.text().catch(() => res.statusText)) as ApiError;
      err.status = res.status;
      err.code = 'HTTP_ERROR';
      throw err;
    }
    return res;
  }

  const body = await res.json();
  if (!res.ok) {
    if (res.status === 401) expireSession();
    const err = new Error(body?.message || 'Something went wrong') as ApiError;
    err.status = res.status;
    err.code = body?.code || 'ERROR';
    err.details = body?.details;
    throw err;
  }
  return body;
}

function qs(params?: Record<string, any>) {
  if (!params) return '';
  const usable = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!usable.length) return '';
  return `?${new URLSearchParams(usable.map(([k, v]) => [k, String(v)])).toString()}`;
}

const url = (path: string, params?: Record<string, any>) => `${BASE}${resolveApiPath(path)}${qs(params)}`;

async function send(method: string, path: string, body?: any) {
  try {
    const res = await fetch(url(path), {
      method,
      headers: headers({ 'Content-Type': 'application/json' }),
      body: body === undefined && method === 'DELETE' ? undefined : JSON.stringify(body ?? {}),
    });
    const out = await handle(res);
    // Saved: every cached list in the application must fetch again.
    announceDataChanged(path);
    return out;
  } catch (err) {
    if ((err as ApiError).status !== undefined) throw err;
    const e = new Error('Could not reach the server. Check your connection and try again.') as ApiError;
    e.status = 0;
    e.code = 'NETWORK';
    throw e;
  }
}

export const api = {
  get: (path: string, params?: Record<string, any>) =>
    fetch(url(path, params), { headers: headers() }).then(handle),
  post: (path: string, body?: any) => send('POST', path, body),
  put: (path: string, body?: any) => send('PUT', path, body),
  patch: (path: string, body?: any) => send('PATCH', path, body),
  del: (path: string, body?: any) => send('DELETE', path, body),

  upload: (path: string, file: File, fields: Record<string, any> = {}) => {
    const form = new FormData();
    form.append('file', file);
    for (const [k, v] of Object.entries(fields)) {
      if (v !== undefined && v !== null) form.append(k, String(v));
    }
    return fetch(url(path), { method: 'POST', headers: headers(), body: form }).then(handle);
  },

  /**
   * Trigger a browser download for an export endpoint. A `body` makes it a
   * POST, for the exports that are asked for rather than addressed.
   */
  download: async (path: string, params?: Record<string, any>, fallbackName = 'download', body?: any) => {
    const res = await fetch(url(path, params), body === undefined
      ? { headers: headers() }
      : { method: 'POST', headers: headers({ 'Content-Type': 'application/json' }), body: JSON.stringify(body) });
    if (!res.ok) {
      if (res.status === 401) expireSession();
      const body = await res.json().catch(() => ({}));
      const err = new Error(body?.message || 'Download failed') as ApiError;
      err.status = res.status;
      err.code = body?.code || 'ERROR';
      throw err;
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = /filename="?([^";]+)"?/.exec(disposition);
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = match?.[1] || fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1500);
  },

  /**
   * Follow a server-sent event stream with the session's headers (EventSource
   * cannot send them). Reconnects with backoff until `stop()` is called;
   * `onState(true)` once the server says it is listening, `onState(false)`
   * whenever the stream is down, so the caller can fall back to polling.
   */
  stream: (path: string, onEvent: (event: string, data: any) => void, onState: (live: boolean) => void = () => {}) => {
    let stopped = false;
    let controller: AbortController | null = null;
    let backoff = 1000;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const run = async () => {
      if (stopped) return;
      controller = new AbortController();
      try {
        const res = await fetch(url(path), { headers: headers({ Accept: 'text/event-stream' }), signal: controller.signal });
        if (res.status === 401) { expireSession(); stopped = true; onState(false); return; }
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        let buffer = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += value;
          let end: number;
          while ((end = buffer.indexOf('\n\n')) >= 0) {
            const block = buffer.slice(0, end);
            buffer = buffer.slice(end + 2);
            let event = 'message';
            const data: string[] = [];
            for (const line of block.split('\n')) {
              if (line.startsWith('event:')) event = line.slice(6).trim();
              else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
            }
            if (!data.length) continue;
            let parsed: any = null;
            try { parsed = JSON.parse(data.join('\n')); } catch { continue; }
            if (event === 'ready') { backoff = 1000; onState(!!parsed?.live); }
            onEvent(event, parsed);
          }
        }
      } catch {
        /* dropped or refused: fall back and retry below */
      }
      if (stopped) return;
      onState(false);
      timer = setTimeout(run, backoff);
      backoff = Math.min(backoff * 2, 30_000);
    };
    void run();
    return {
      stop: () => {
        stopped = true;
        if (timer) clearTimeout(timer);
        controller?.abort();
      },
    };
  },
};
